"use client";

import { TypeCalendlyEmbed } from "@contentful/types";
import { appendCalendlyUtm } from "@yardzen/js-util";
import { cn } from "@yardzen/ui/lib/utils";
import type { ChainModifiers, LocaleCode } from "contentful";
import * as React from "react";

export type CalendlyUrl =
  | `https://calendly.com/${string}`
  | `https://www.calendly.com/${string}`;

export type CalendlyEmbedFields = {
  url: CalendlyUrl | string;
  title?: string;
  fixedHeight?: number;
  redirectTo?: string;
};

export type CalendlyEmbedProps<
  Mods extends ChainModifiers = "WITHOUT_UNRESOLVABLE_LINKS",
  Loc extends LocaleCode = LocaleCode,
> =
  | { className?: string; embed: CalendlyEmbedFields; calendlyEmbed?: never }
  | {
      className?: string;
      calendlyEmbed: TypeCalendlyEmbed<Mods, Loc>;
      embed?: never;
    };

const ALLOWED_HOSTS = ["calendly.com", "www.calendly.com"] as const;
const EMBED_DOMAIN = process.env.NEXT_PUBLIC_EMBED_DOMAIN ?? "yardzen.com";

const YARDZEN_STYLE_DEFAULTS = {
  background_color: "f3f3ed",
  primary_color: "4e5a44",
} as const;

function isAllowedHost(host: string): boolean {
  return (ALLOWED_HOSTS as readonly string[]).includes(host.toLowerCase());
}

function buildSrc(u: URL): string {
  const copy = new URL(u.toString());
  const sp = copy.searchParams;

  if (!sp.has("hide_event_type_details"))
    sp.set("hide_event_type_details", "1");
  if (!sp.has("hide_gdpr_banner")) sp.set("hide_gdpr_banner", "1");
  if (!sp.has("embed_type")) sp.set("embed_type", "Inline");
  if (!sp.has("embed_domain")) sp.set("embed_domain", EMBED_DOMAIN);

  // Apply Yardzen brand colors as defaults if not already specified in URL
  if (!sp.has("background_color"))
    sp.set("background_color", YARDZEN_STYLE_DEFAULTS.background_color);
  if (!sp.has("primary_color"))
    sp.set("primary_color", YARDZEN_STYLE_DEFAULTS.primary_color);
  // text_color: only preserve if already present, no default applied

  const sorted = new URLSearchParams(
    Array.from(sp.entries()).sort(([a], [b]) => a.localeCompare(b)),
  ).toString();

  return `${copy.origin}${copy.pathname}${sorted ? `?${sorted}` : ""}`;
}

function normalize<Mods extends ChainModifiers, Loc extends LocaleCode>(
  props: CalendlyEmbedProps<Mods, Loc>,
): CalendlyEmbedFields | null {
  if ("embed" in props) return props.embed ?? null;
  if ("calendlyEmbed" in props) {
    const f = props.calendlyEmbed.fields as {
      url: string;
      title?: string | null;
      fixedHeight?: number | null;
      redirectTo?: string | null;
    };
    return {
      url: f.url,
      title: f.title ?? undefined,
      fixedHeight: f.fixedHeight ?? undefined,
      redirectTo: f.redirectTo ?? undefined,
    };
  }
  return null;
}

export function CalendlyEmbed<
  Mods extends ChainModifiers = "WITHOUT_UNRESOLVABLE_LINKS",
  Loc extends LocaleCode = LocaleCode,
>(props: CalendlyEmbedProps<Mods, Loc>) {
  const { className } = props as { className?: string };
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  const data = React.useMemo(() => normalize(props), [props]);

  const parsedUrl = React.useMemo<URL | null>(() => {
    try {
      if (!data?.url) return null;
      const u = new URL(data.url);
      return isAllowedHost(u.hostname) ? u : null;
    } catch {
      return null;
    }
  }, [data?.url]);

  const [pageUrl, setPageUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    setPageUrl(window.location.href);
  }, []);

  const src = React.useMemo<string | null>(() => {
    if (!parsedUrl || pageUrl === null) return null;
    return buildSrc(
      new URL(
        appendCalendlyUtm(parsedUrl.toString(), "Contentful Module", pageUrl),
      ),
    );
  }, [parsedUrl, pageUrl]);

  const DEFAULT_HEIGHT = 500;
  const [autoHeight, setAutoHeight] = React.useState<number | undefined>(
    data?.fixedHeight ? undefined : DEFAULT_HEIGHT,
  );

  React.useEffect(() => {
    if (!src) return;

    const onMessage = (e: MessageEvent) => {
      try {
        const host = new URL(e.origin).hostname;
        if (!/calendly\.com$/i.test(host)) return;
        if (
          iframeRef.current &&
          iframeRef.current.contentWindow &&
          e.source !== iframeRef.current.contentWindow
        )
          return;

        const anyData = e.data as {
          height?: unknown;
          payload?: {
            height?: unknown;
            event?: { uri?: string };
            invitee?: { uri?: string };
          };
          event?: string;
          eventName?: string;
        };

        const h = (anyData.height ?? anyData.payload?.height) as
          | number
          | undefined;
        if (typeof h === "number" && h > 400 && h < 2000) setAutoHeight(h);

        const name = anyData.event || anyData.eventName;
        if (name === "calendly.event_scheduled" && data?.redirectTo) {
          const qp = new URLSearchParams();
          const eventUri = anyData.payload?.event?.uri;
          const inviteeUri = anyData.payload?.invitee?.uri;
          if (eventUri) qp.set("eventUri", eventUri);
          if (inviteeUri) qp.set("inviteeUri", inviteeUri);
          window.location.assign(
            qp.toString()
              ? `${data.redirectTo}?${qp.toString()}`
              : data.redirectTo,
          );
        }
      } catch {
        // ignore malformed messages
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [src, data?.redirectTo]);

  const computedHeight = data?.fixedHeight ?? autoHeight;

  if (!src) return null;

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-2xl bg-white",
        className,
      )}
      style={computedHeight ? { height: computedHeight } : undefined}
    >
      <iframe
        ref={iframeRef}
        src={src}
        title={data?.title ?? "Calendly scheduler"}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allow="geolocation; microphone; camera; clipboard-read; clipboard-write;"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation-by-user-activation allow-popups-to-escape-sandbox"
        className="absolute inset-0 h-full w-full border-0"
      />
    </div>
  );
}
